/**
 * LandingPage — public marketing surface at "/".
 *
 * Editorial / Swiss high-contrast composition matching the in-app
 * Business Plan aesthetic. Builds on Cabinet Grotesk display + Satoshi
 * body, charcoal #4A4458, lilac #7C5FB0, emerald #059669.
 *
 * Sections:
 *   1. Sticky header  — brand mark + Sign in + Get started CTAs
 *   2. Hero           — bold tagline + supporting copy + dual CTA
 *   3. Stats strip    — four KPIs (one screen, hard numbers)
 *   4. Feature grid   — 6 product pillars (one card per pillar)
 *   5. How it works   — 3-step founder journey
 *   6. Social proof   — three founder quotes + a marketplace highlight
 *   7. Pricing teaser — 3 tiers (Standard featured) with 7-day free preview note
 *   8. Final CTA      — full-width emerald band
 *   9. Footer         — links + copyright
 *
 * Authenticated users redirect to /dashboard so a logged-in founder
 * never lands on the marketing page (App.js gate handles `/`).
 */
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import ProductDemoPlayer from "../components/demo/ProductDemoPlayer";
import Logo from "../components/Logo";
import ExitIntentStrip from "../components/ExitIntentStrip";
import FeatureVideo from "../components/FeatureVideo";
import AttributionDemoReel from "../components/AttributionDemoReel";
import MarketingJourneyDemoReel from "../components/MarketingJourneyDemoReel";
import FounderOnboardingReel from "../components/FounderOnboardingReel";
import IntroStory from "../components/IntroStory";
import { PlanComparisonMatrix } from "../components/PlanComparisonMatrix";
import { CompetitorMatrix } from "../components/CompetitorMatrix";
import {
  ArrowRight,
  PlayCircle,
  Play,
  Sparkles,
  Target,
  ShieldCheck,
  ListChecks,
  TrendingUp,
  Users,
  Briefcase,
  Landmark,
  FolderOpen,
  Package,
  Check,
  Star,
  Eye,
  RefreshCw,
  Send,
  BarChart3,
  LayoutTemplate,
  FlaskConical,
  Repeat,
  Rocket,
  Globe,
  Bot,
  Megaphone,
  HeartPulse,
  Layers,
  Route,
} from "lucide-react";

/**
 * Imperatively injects the LandingPage JSON-LD blocks into `document.head`.
 *
 * We can't rely on `react-helmet-async` to hoist `<script type="application/ld+json">`
 * children — its child reconciler drops or misplaces them (they land in `<body>`),
 * and Gemini / ChatGPT / Perplexity only reliably parse JSON-LD that lives inside
 * the document `<head>`. So we side-step Helmet and imperatively append the tags
 * on mount, remove them on unmount. `data-owner` lets us dedupe safely under
 * React StrictMode's double-invocation.
 *
 * Bug fixed: Gemini answered "how much does Three Little Birds cost?" with wrong
 * or vague pricing because the SPA shipped no structured pricing. Emitting a
 * dedicated `Product` + `AggregateOffer` graph — with a `UnitPriceSpecification`
 * (`billingIncrement`, `unitCode: "MON"`) on every tier — gives answer engines a
 * canonical machine-readable pricing surface.
 */
function LandingJsonLd({ productLd, orgLd, faqLd }) {
  useEffect(() => {
    const OWNER = "landing-page-jsonld";
    const payloads = [
      { key: "product", data: productLd },
      { key: "organization", data: orgLd },
      { key: "faq", data: faqLd },
    ];
    document.head
      .querySelectorAll(`script[type="application/ld+json"][data-owner="${OWNER}"]`)
      .forEach((n) => n.remove());
    const tags = payloads.map(({ key, data }) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.dataset.owner = OWNER;
      s.dataset.kind = key;
      s.textContent = JSON.stringify(data);
      document.head.appendChild(s);
      return s;
    });
    return () => tags.forEach((t) => {
      if (t && t.parentNode) t.parentNode.removeChild(t);
    });
  }, [productLd, orgLd, faqLd]);
  return null;
}

export default function LandingPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Feature-Film modal state is owned by the root so the same player can
  // be triggered from multiple surfaces (Hero "Watch the intro" + the
  // dedicated film section + the final CTA).
  const [videoOpen, setVideoOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  // The "See it in motion" section opens the IntroStory first, which then
  // rolls seamlessly into the feature film (see IntroStory autoToVideoMs).
  const [introOpen, setIntroOpen] = useState(false);
  // Start the film with sound only when the viewer arrives via the IntroStory
  // hand-off (they opted into the cinematic experience).
  const [videoUnmuted, setVideoUnmuted] = useState(false);

  // Logged-in users skip the marketing surface and land on their workspace.
  // Loading state stays neutral so the redirect happens silently after
  // hydration without a flash of the public page.
  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [loading, user, navigate]);

  // Shareable deep-link: /?film=1 opens the feature film immediately (with
  // sound). We deliberately DON'T scrub the param here — under React's dev
  // StrictMode the initial mount is thrown away, and scrubbing on open would
  // strip the param before the real mount runs (film never shows). Instead we
  // leave ?film=1 in the URL and scrub it when the viewer closes the film.
  useEffect(() => {
    if (searchParams.get("film") === "1") {
      setVideoUnmuted(true);
      setVideoOpen(true);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const openVideo = () => { setVideoUnmuted(false); setVideoOpen(true); };
  // Closing the film scrubs the ?film=1 deep-link param (replace:true so a
  // back-nav can't re-fire it) once the viewer is done watching.
  const closeVideo = () => {
    setVideoOpen(false);
    if (searchParams.get("film") === "1") {
      const next = new URLSearchParams(searchParams);
      next.delete("film");
      setSearchParams(next, { replace: true });
    }
  };
  // IntroStory → FeatureVideo bridge for the "See it in motion" section.
  const openIntro = () => setIntroOpen(true);
  const advanceToVideo = () => { setIntroOpen(false); setVideoUnmuted(true); setVideoOpen(true); };
  // FeatureVideo's CTA button bottoms out into the sign-in flow — keep
  // that contract consistent with the Login experience.
  const videoCta = () => {
    setVideoOpen(false);
    navigate("/login");
  };

  // -------------------------- JSON-LD (Jul 2026) --------------------------
  // Answer engines (Gemini, ChatGPT, Perplexity) misquote our pricing when
  // asked "how much does Three Little Birds cost?" because the SPA emits no
  // structured price graph. We publish a `Product` with an `AggregateOffer`
  // whose child `Offer`s each carry a `UnitPriceSpecification` — the exact
  // shape schema.org recommends for recurring SaaS pricing — plus a matching
  // `Organization` and site-level `FAQPage`. Injected imperatively into
  // <head> via <LandingJsonLd/> (Helmet can't hoist <script> children).
  const productLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": "https://threelilbrds.com/#product",
    name: "Three Little Birds",
    alternateName: ["3LilBrds", "threelilbrds", "Three Lil' Birds"],
    category: "Business planning + execution software (SaaS)",
    url: "https://threelilbrds.com/",
    image: "https://threelilbrds.com/og-card.png",
    description:
      "Three Little Birds is an all-in-one business launch and operate OS for first-time founders and small businesses. It pairs Three Little Birds-drafted business plans and lender-grade 3-statement financials with a full execution layer — a drag-and-drop launch board + Gantt roadmap with critical path, CRM + deal pipeline, email & SMS campaigns, Three Little Birds-drafted ad copy for Meta/Google/TikTok/LinkedIn with server-side conversion tracking and CRM attribution, a jurisdiction-aware compliance tracker, a grants finder (Grants.gov, SAM.gov Assistance Listings, Perplexity web citations, Three Little Birds matcher), a Loan modeler with bank-ready PDF export, an Investor Marketplace, a document vault with Three Little Birds OCR, and an Founder Mentor with real-time collaboration.",
    brand: { "@type": "Brand", name: "Three Little Birds" },
    manufacturer: { "@type": "Organization", name: "Three Little Birds", url: "https://threelilbrds.com/" },
    aggregateRating: { "@type": "AggregateRating", ratingValue: "4.8", ratingCount: "127", bestRating: "5", worstRating: "1" },
    offers: {
      "@type": "AggregateOffer",
      offerCount: "4",
      lowPrice: "0",
      highPrice: "35",
      priceCurrency: "USD",
      url: "https://threelilbrds.com/#pricing",
      offers: [
        {
          "@type": "Offer",
          "@id": "https://threelilbrds.com/#offer-free-preview",
          name: "Free preview",
          description: "7-day full preview of the Standard tier, no credit card required. Cancel anytime.",
          price: "0",
          priceCurrency: "USD",
          category: "free",
          availability: "https://schema.org/InStock",
          url: "https://threelilbrds.com/#pricing",
          priceSpecification: {
            "@type": "UnitPriceSpecification",
            price: "0",
            priceCurrency: "USD",
            unitCode: "MON",
            referenceQuantity: { "@type": "QuantitativeValue", value: "1", unitCode: "MON" },
            billingIncrement: 1,
            billingDuration: 7,
            valueAddedTaxIncluded: false,
          },
        },
        {
          "@type": "Offer",
          "@id": "https://threelilbrds.com/#offer-basic",
          name: "Basic",
          description: "Plan + roadmap + watermarked PDF. Solo founders piloting an idea.",
          price: "15",
          priceCurrency: "USD",
          category: "subscription",
          availability: "https://schema.org/InStock",
          priceValidUntil: "2026-12-31",
          url: "https://threelilbrds.com/#pricing",
          priceSpecification: {
            "@type": "UnitPriceSpecification",
            price: "15",
            priceCurrency: "USD",
            unitCode: "MON",
            referenceQuantity: { "@type": "QuantitativeValue", value: "1", unitCode: "MON" },
            billingIncrement: 1,
            valueAddedTaxIncluded: false,
          },
        },
        {
          "@type": "Offer",
          "@id": "https://threelilbrds.com/#offer-standard",
          name: "Standard",
          description: "Everything in Basic plus budget tracking, compliance, CRM, documents, lender package, benchmarks, and the investor marketplace.",
          price: "25",
          priceCurrency: "USD",
          category: "subscription",
          availability: "https://schema.org/InStock",
          priceValidUntil: "2026-12-31",
          url: "https://threelilbrds.com/#pricing",
          priceSpecification: {
            "@type": "UnitPriceSpecification",
            price: "25",
            priceCurrency: "USD",
            unitCode: "MON",
            referenceQuantity: { "@type": "QuantitativeValue", value: "1", unitCode: "MON" },
            billingIncrement: 1,
            valueAddedTaxIncluded: false,
          },
        },
        {
          "@type": "Offer",
          "@id": "https://threelilbrds.com/#offer-advanced",
          name: "Advanced",
          description: "Everything in Standard plus driver-based forecasting, what-if scenarios, investor scenarios, and Email & SMS campaigns.",
          price: "35",
          priceCurrency: "USD",
          category: "subscription",
          availability: "https://schema.org/InStock",
          priceValidUntil: "2026-12-31",
          url: "https://threelilbrds.com/#pricing",
          priceSpecification: {
            "@type": "UnitPriceSpecification",
            price: "35",
            priceCurrency: "USD",
            unitCode: "MON",
            referenceQuantity: { "@type": "QuantitativeValue", value: "1", unitCode: "MON" },
            billingIncrement: 1,
            valueAddedTaxIncluded: false,
          },
        },
      ],
    },
  };

  const orgLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://threelilbrds.com/#organization",
    name: "Three Little Birds",
    url: "https://threelilbrds.com/",
    logo: "https://threelilbrds.com/logo.png",
    description: "All-in-one business launch and operate OS for first-time founders and small businesses.",
  };

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "How much does Three Little Birds cost?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Three Little Birds has four tiers billed monthly in USD: a 7-day Free preview of the Standard tier with no credit card required, Basic at $15/month, Standard at $25/month, and Advanced at $35/month. Firms serving multiple client founders can pick from Advisor / white-label tiers starting at $149/month.",
        },
      },
      {
        "@type": "Question",
        name: "Is there a free trial of Three Little Birds?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Every new account gets a 7-day full preview of the Standard tier with no credit card required — cancel anytime.",
        },
      },
      {
        "@type": "Question",
        name: "What is included in the Standard plan?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Standard ($25/month) includes everything in Basic plus budget tracking, QuickBooks & Xero sync, a jurisdiction-aware compliance tracker, a native CRM + deal pipeline, a document library, a lender-ready financial package, industry benchmarks, an investor marketplace listing, and a customizable Pitch Deck.",
        },
      },
      {
        "@type": "Question",
        name: "What does the Advanced plan add over Standard?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Advanced ($35/month) adds driver-based 3-year monthly forecasting (36-month CSV export), a what-if scenario planner, investor scenarios (equity / income / hybrid), Email & SMS campaigns, unlimited business plans, and priority support.",
        },
      },
      {
        "@type": "Question",
        name: "Does Three Little Birds offer a white-label option for advisors?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Advisor / white-label tiers let firms serving multiple client founders put their logo and colors on client PDFs, decks and share cards, with a co-branded client login, a cohort dashboard with per-client health scores, and full Standard features on every client seat. Advisor Starter is $149/month for up to 10 client seats.",
        },
      },
    ],
  };

  return (
    <div
      className="min-h-screen bg-white text-neutral-900 antialiased scroll-smooth"
      data-testid="landing-page"
    >
      <Helmet>
        <title>Three Little Birds — the all-in-one OS to plan, launch &amp; grow your business</title>
        <meta name="description" content="Three Little Birds takes founders from business plan to launch to growth: Three Little Birds plan writing, lender-ready financials & forecasting, plus roadmap, CRM, grants and an Founder Mentor. From $15/mo. Free preview, no card." />
        <link rel="canonical" href="https://threelilbrds.com/" />
        <meta property="og:title" content="Three Little Birds — plan, launch & grow your business" />
        <meta property="og:description" content="The all-in-one OS for first-time founders. From $15/mo with a free preview." />
        <meta property="og:url" content="https://threelilbrds.com/" />
      </Helmet>
      <LandingJsonLd
        productLd={productLd}
        orgLd={orgLd}
        faqLd={faqLd}
      />
      <ExitIntentStrip />
      <Header />
      <Hero onPlayVideo={openVideo} onWatchDemo={() => setDemoOpen(true)} />
      <StatsStrip />
      <HowItWorks />
      <FounderOnboardingSpotlight />
      <TwoFoundersSection />
      <FeatureFilm onPlayVideo={openIntro} />
      <FeatureGrid />
      <SocialProof />
      <PricingTeaser />
      <AboutFaq />
      <FinalCta />
      <Footer />

      <FeatureVideo
        open={videoOpen}
        onClose={closeVideo}
        onPrimaryCta={videoCta}
        primaryCtaLabel="Start your free preview"
        startUnmuted={videoUnmuted}
      />

      <IntroStory
        open={introOpen}
        onClose={() => setIntroOpen(false)}
        onPrimaryCta={advanceToVideo}
        primaryCtaLabel="Watch the feature film"
        autoToVideoMs={3500}
      />

      <ProductDemoPlayer
        open={demoOpen}
        onClose={() => setDemoOpen(false)}
        onCta={() => { setDemoOpen(false); navigate("/login"); }}
      />
    </div>
  );
}

/* ------------------------------ Header ------------------------------ */

function Header() {
  return (
    <header
      className="sticky top-0 z-30 backdrop-blur bg-white/85 border-b border-neutral-200"
      data-testid="landing-header"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="landing-brand-link">
          <Logo variant="mark" height={32} />
          <span className="font-display font-black tracking-tighter text-base hidden sm:inline">
            Three Little Birds
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-1" data-testid="landing-nav-menu">
          {[
            { label: "Product", href: "#product", testid: "landing-nav-product" },
            { label: "Features", href: "#features", testid: "landing-nav-features" },
            { label: "Founders", href: "#solutions", testid: "landing-nav-solutions" },
            { label: "How We Compare", href: "#compare", testid: "landing-nav-compare" },
            { label: "Pricing", href: "#pricing", testid: "landing-nav-pricing" },
          ].map((item) => (
            <a
              key={item.testid}
              href={item.href}
              data-testid={item.testid}
              className="text-[11px] uppercase tracking-[0.2em] font-bold text-neutral-700 hover:text-[#7C5FB0] px-3 py-2 transition-colors"
            >
              {item.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <Link
            to="/login"
            data-testid="landing-signin-btn"
            className="text-[11px] uppercase tracking-[0.2em] font-bold text-neutral-700 hover:text-neutral-900 px-3 py-2"
          >
            Sign in
          </Link>
          <Link to="/login" data-testid="landing-getstarted-btn">
            <Button className="rounded-none bg-[#4A4458] hover:bg-[#353042] text-white text-[11px] uppercase tracking-[0.2em] font-bold h-10 px-4">
              Get started
              <ArrowRight className="w-3.5 h-3.5 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

/* ------------------------------- Hero ------------------------------- */

/**
 * Jul 2026 · Animated brand logo in the hero.
 *
 * Replaces the abstract SVG bird flock (which was a stand-in placeholder)
 * with the actual Three Little Birds logo mark, animated with a one-shot
 * entrance flutter followed by a continuous, subtle bob-and-tilt loop so
 * the mark feels alive without stealing focus. Decorative — aria-hidden,
 * pointer-events-none, honours `prefers-reduced-motion`.
 */
function LandingHeroLogo() {
  return (
    <img
      src="/logo-mark.png"
      alt=""
      aria-hidden="true"
      draggable={false}
      data-testid="landing-hero-logo"
      className="hidden md:block absolute top-6 right-8 lg:right-16 pointer-events-none w-32 lg:w-40 xl:w-48 h-auto select-none landing-logo-mark"
    />
  );
}

function Hero({ onPlayVideo, onWatchDemo }) {
  return (
    <section
      className="relative overflow-hidden"
      data-testid="landing-hero"
    >
      {/* Subtle grain + spotlight background. Editorial, not "Three Little Birds slop". */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none landing-hero-ambient"
      />
      {/* Feb 2026 · painterly overlays — a fine feTurbulence grain to
          break up the flat white cardstock, plus three "little birds"
          flying diagonally across the hero as a subtle brand callback.
          Both decorative; both `pointer-events-none` + `aria-hidden`. */}
      <div
        aria-hidden="true"
        data-testid="landing-hero-grain"
        className="absolute inset-0 pointer-events-none opacity-[0.045] mix-blend-multiply landing-hero-grain-bg"
      />
      {/* Jul 2026 · animated brand logo — replaces the placeholder SVG
          bird flock + fourth-returning-bird sweep. Same top-right
          position, but now uses the actual Three Little Birds mark
          with a one-shot entrance flutter + continuous bob-and-tilt
          loop. Decorative; `pointer-events-none` + `aria-hidden`. */}
      <LandingHeroLogo />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 lg:pt-28 lg:pb-32">
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#7C5FB0] mb-6 inline-flex items-center gap-2">
          <Sparkles className="w-3 h-3" />
          Three Little Birds-drafted business plans · Hybrid health scoring · Investor-ready
        </div>
        <h1
          data-testid="landing-hero-headline"
          className="font-display font-black tracking-tighter text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] max-w-5xl"
        >
          Dream it.{" "}
          <span className="italic text-[#7C5FB0]">Build it.</span>{" "}
          Launch it.
        </h1>
        <p
          data-testid="landing-hero-sub"
          className="mt-8 text-lg sm:text-xl text-neutral-600 max-w-2xl leading-relaxed"
        >
          A small-business operating system that takes you from rough idea to
          investor-grade plan, week-one budget, compliance tracker, and a
          living roadmap — without the spreadsheet hell.
        </p>
        <div className="mt-10 flex flex-wrap items-center gap-3">
          <Link to="/login" data-testid="landing-hero-cta-primary">
            <Button className="rounded-none bg-[#4A4458] hover:bg-[#353042] text-white h-12 px-6 text-[12px] uppercase tracking-[0.25em] font-bold">
              Start your free preview
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <button
            type="button"
            onClick={onWatchDemo}
            data-testid="landing-hero-watch-demo"
            className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.25em] font-bold text-white bg-[#7C5FB0] hover:bg-[#6A4F9C] h-12 px-5"
          >
            <PlayCircle className="w-4 h-4" />
            Watch the 60-sec demo
          </button>
          <button
            type="button"
            onClick={onPlayVideo}
            data-testid="landing-hero-cta-secondary"
            className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.25em] font-bold text-neutral-700 hover:text-neutral-900 h-12 px-2"
          >
            <PlayCircle className="w-4 h-4" />
            Watch the intro
          </button>
        </div>
        <div
          className="mt-6 text-[11px] uppercase tracking-[0.2em] text-neutral-500 font-bold"
          data-testid="landing-hero-fineprint"
        >
          No credit card · 7-day preview tier · Pick a plan when you're ready
        </div>
      </div>
    </section>
  );
}

/* ------------------------- Two founders / solutions ------------------------- */
// Speaks to both audiences explicitly: new founders who need to LAUNCH, and
// experienced operators who need to OPTIMIZE what they already run.

const FOUNDER_PATHS = [
  {
    testid: "landing-path-launch",
    eyebrow: "New founders",
    Icon: Rocket,
    accent: "#7C5FB0",
    title: "Launch with a plan that holds up.",
    body: "Turn a rough idea into an investor-grade business plan, a week-one budget, and a living launch roadmap — in an afternoon, not a quarter. The drafts every section; you refine, and a hybrid Health Score tells you exactly when you're ready to open the doors (or pitch).",
    points: [
      "Guided 5-step wizard → 14-section plan",
      "Three-statement financials + revenue projections",
      "Launch board with dependencies",
      "Clone a curated Plan Template to skip the blank page",
    ],
    cta: "Start your plan",
  },
  {
    testid: "landing-path-optimize",
    eyebrow: "Experienced founders",
    Icon: TrendingUp,
    accent: "#0D9488",
    title: "Operate sharper, every month.",
    body: "Already trading? Launching isn't the finish line. Pressure-test new or existing products with real unit economics, get prioritized optimization moves grounded in your numbers, and run continuous improvement cycles — so the business compounds instead of drifting.",
    points: [
      "Feasibility analyzer with go / caution / no-go verdicts",
      "Business optimization moves, highest-impact first",
      "Continuous process improvement with measured baselines",
      "Plan-vs-actual performance dashboard + Three Little Birds monthly review",
    ],
    cta: "Explore the operate toolkit",
  },
];

function TwoFoundersSection() {
  return (
    <section className="border-t border-neutral-200 bg-neutral-50 scroll-mt-20" id="solutions" data-testid="landing-two-founders">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="max-w-3xl">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#7C5FB0] mb-4">
            One platform · two journeys
          </div>
          <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">
            Whether you&apos;re starting up or scaling up.
          </h2>
          <p className="mt-5 text-lg text-neutral-600 leading-relaxed">
            Three Little Birds meets founders where they are — get a brand-new venture
            launch-ready, then keep operating it like a system that compounds.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {FOUNDER_PATHS.map((p) => {
            const Icon = p.Icon;
            return (
              <div
                key={p.testid}
                data-testid={p.testid}
                className="bg-white border-2 border-neutral-900 p-8 lg:p-10 flex flex-col shadow-[8px_8px_0_0_#0A0A0A]"
              >
                <div className="flex items-center gap-3">
                  <span
                    className="w-11 h-11 flex items-center justify-center text-white flex-shrink-0"
                    style={{ backgroundColor: p.accent }}
                  >
                    <Icon className="w-5 h-5" />
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: p.accent }}>
                    {p.eyebrow}
                  </span>
                </div>
                <h3 className="font-display font-black text-3xl tracking-tighter leading-[1.05] mt-6">
                  {p.title}
                </h3>
                <p className="text-neutral-600 mt-4 leading-relaxed">{p.body}</p>
                <ul className="mt-6 space-y-2.5 flex-1">
                  {p.points.map((pt, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-neutral-800">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: p.accent }} />
                      {pt}
                    </li>
                  ))}
                </ul>
                <Link to="/login" data-testid={`${p.testid}-cta`} className="mt-8">
                  <Button
                    className="rounded-none text-white h-12 px-6 text-[12px] uppercase tracking-[0.25em] font-bold w-full sm:w-auto"
                    style={{ backgroundColor: p.accent }}
                  >
                    {p.cta}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}


/* ---------------------------- Stats strip --------------------------- */

function FounderOnboardingSpotlight() {
  return (
    <section
      className="border-t border-neutral-200 bg-white scroll-mt-20"
      id="onboarding-reel"
      data-testid="landing-founder-onboarding-spotlight"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-14 items-center">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-emerald-700 mb-3 inline-flex items-center gap-2">
              <Rocket className="w-3 h-3" />
              First 60 seconds
            </div>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[1.02] mb-5">
              From blank page to a live business plan in 60 seconds.
            </h2>
            <p className="text-neutral-600 leading-relaxed mb-5">
              Sign up, answer six quick questions in the wizard, watch the draft your plan section by section, and land on a live Business Plan with KPIs, Market analysis, Competitors, and Customer acquisition &mdash; ready to edit or share.
            </p>
            <ul className="space-y-2.5 text-sm text-neutral-800 mb-8">
              {[
                "No credit card — free preview tier starts instantly",
                "6-question wizard tailored to your industry and geography",
                "drafts 14 plan sections in parallel with a live progress bar",
                "First plan section renders before your coffee's cool",
              ].map((pt, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-600" />
                  {pt}
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap items-center gap-3">
              <Link to="/login" data-testid="landing-onboarding-reel-cta-primary">
                <Button className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white h-12 px-6 text-[12px] uppercase tracking-[0.25em] font-bold">
                  Start free — no card
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link
                to="/blog/attribution-loop"
                data-testid="landing-onboarding-reel-cta-secondary"
                className="text-[12px] uppercase tracking-[0.25em] font-bold text-neutral-700 hover:text-black underline underline-offset-4"
              >
                See what founders build →
              </Link>
            </div>
          </div>
          <div>
            <FounderOnboardingReel />
          </div>
        </div>
      </div>
    </section>
  );
}


/* ---------------------------- Stats strip --------------------------- */

function StatsStrip() {
  const stats = [
    { k: "14", label: "Business plan sections" },
    { k: "50/50", label: "Hybrid health scoring" },
    { k: "<60s", label: "From wizard to first draft" },
    { k: "7d", label: "Free preview tier — no card" },
  ];
  return (
    <section
      className="border-y border-neutral-900 bg-neutral-50"
      data-testid="landing-stats"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4">
        {stats.map((s, i) => (
          <div
            key={s.label}
            className={`p-6 lg:p-8 ${i > 0 ? "lg:border-l border-neutral-900" : ""} ${i % 2 === 1 ? "border-l border-neutral-900 lg:border-l" : ""} ${i >= 2 ? "border-t border-neutral-900 lg:border-t-0" : ""}`}
            data-testid={`landing-stat-${i}`}
          >
            <div className="font-display font-black text-4xl sm:text-5xl tracking-tighter">
              {s.k}
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-neutral-500 mt-2">
              {s.label}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* --------------------------- Feature grid --------------------------- */

const FEATURES = [
  {
    icon: Sparkles,
    eyebrow: "Dream",
    title: "Three Little Birds-drafted business plan",
    body: "Refined description, market analysis with live-cited research, competitive landscape, pricing model with rationale, 3-year revenue projection, customer acquisition tactics, and infrastructure — drafted in under a minute from a five-step wizard.",
    img: "02-plan-top.jpg",
    imgAlt: "Three Little Birds business plan page showing the executive summary, health score and sidebar of every section",
  },
  {
    icon: Target,
    eyebrow: "Health",
    title: "Hybrid Plan Health score",
    body: "50% deterministic (runway, budget completeness, revenue realism, roadmap balance — 25 pts each) + 50% qualitative LLM. Industry benchmarks show how you stack up, and every leg links back to the exact section that moves it.",
  },
  {
    icon: HeartPulse,
    eyebrow: "Signals",
    title: "Six Health scores, one glance",
    body: "Every founder sees the same 0–100 stack — Plan Health (hybrid Three Little Birds+data), Launch Health (Plan · Roadmap · Compliance), Business Health (Launch · Funding · Marketing), Operating Health (Revenue · Funding · Marketing · Launch, ongoing plans only), Financial Health (Runway · Capital · Net vs plan · Marketing) and Growth Health (Audience · Reach · Conversion · Pipeline). Each leg is a click-through pill wired to the feature that moves the number — the score is never a dead-end.",
    img: "23-hub.jpg",
    imgAlt: "Three Little Birds Business Hub showing six Health scores with per-leg breakdowns — Launch, Business, Operating, Funding and Marketing tiles",
  },
  {
    icon: Sparkles,
    eyebrow: "Command Center",
    title: "Business Hub — one screen you actually live in",
    body: "The daily home base. A phase ribbon that walks Idea → Plan → Roadmap → Launch → Operate. A composite Business Health score that goes red the moment your live plan slips below 65%. And one deep-linked Move of the Week — the single act-on-this-next lever, chosen automatically from the weakest leg — so you never open the app wondering what to do first.",
    img: "23-hub.jpg",
    imgAlt: "Three Little Birds Business Hub for Bean & Bloom Coffee — journey ribbon on Plan phase, Business Health 39% Fragile with per-leg breakdown, and a Move of the Week card highlighting the next lever",
  },
  {
    icon: Layers,
    eyebrow: "See it all",
    title: "Strategic Plan · Hierarchical Overview",
    body: "The moment your plan graduates from Launching → Operating, the Business Plan becomes a Strategic Plan — a live, draggable SVG chart of Business Drivers → Strategic Goals → Projects → Roadmap Tasks on one screen. A pinnable Mini-Map lets you scrub around big plans (both axes), overflow indicators warn when a goal has more projects than fit, and every node is a click-through into the source surface. Launching plans get the same treatment via the Pre-Launch Overview (Business Plan → Compliance → Health → Goals). Edges are health-coloured — green healthy, amber drifting, red red-line — so a slipping goal jumps off the screen. The default view (Overview vs. Traditional sections) is a Settings preference so power-users can lock in the map.",
  },
  {
    icon: Route,
    eyebrow: "Close the loop",
    title: "Attribution Loop — Goals · Projects · Deals · Campaigns",
    body: "Every closed-won deal and every marketing campaign carries an origin_ref back to the Strategic Goal or Roadmap Task that sparked it. A 4-week revenue sparkline sits on every Goal card. Filter the CRM pipeline or the Campaign list by any Goal with one click. Prefill a new Deal or Campaign from a Goal so the origin link happens automatically. Result: for the first time, you can answer 'how much revenue did this Goal actually produce?' — without a spreadsheet, without manual tagging, without leaving the app.",
  },
  {
    icon: FlaskConical,
    eyebrow: "Run experiments",
    title: "Projects Board — drag-and-drop growth board (Advanced)",
    body: "Advanced founders get a Projects board on every operating plan — a four-column board (Ideas · Running · Won · Lost) for the growth initiatives you run against your Strategic Goals. Drag a card across columns to update status (Won triggers confetti + auto-nudges the linked Goal's progress). Each project has its own mini-roadmap of tasks, health ring, owner and due dates. Start from scratch or pick a proven template from the built-in library.",
  },
  {
    icon: Package,
    eyebrow: "Offer",
    title: "Products & Services catalog",
    body: "The living catalog of what your business sells — product / service, name, description, price, cost, launch status (Draft / Active) and hero image. Click a card to edit inline; toggle Draft to Active with one click and Standard+ automatically flows the revenue into your Goals and forecast. Each item ties back to a customer segment and a pricing scenario so your P&L is never disconnected from the offer.",
    img: "21-products.jpg",
    imgAlt: "Three Little Birds Products & Services catalog with cards showing image, price, cost, and active/draft toggles",
  },
  {
    icon: ListChecks,
    eyebrow: "Execute",
    title: "Launch board with critical path",
    body: "Drag-and-drop execution timeline that respects dependencies. Stale-section detection nudges you when a wizard input changes downstream sections. Toggle to the Gantt view to see the critical path and slip risk against your launch date.",
    img: "10-gantt.jpg",
    imgAlt: "Three Little Birds Gantt timeline with dependency arrows and a highlighted critical path across launch milestones",
  },
  {
    icon: Bot,
    eyebrow: "Get-it-done",
    title: "Service Agents — in-app vendor assistants for every task",
    body: "Standard+ pins a task-scoped Three Little Birds agent to every roadmap card, wired into a hand-vetted catalog of 23+ real providers — Stripe Atlas & ZenBusiness for incorporation, Mercury for banking, Gusto & Rippling for payroll, Shopify for storefronts, Canva & Looka for branding, Mailchimp & HubSpot for outreach, Calendly & Notion for ops. Two modes: Chat for grounded step-by-step guidance on that exact filing, and Quick which turns 3–5 answers into a ready-to-paste draft (EIN application, business-account checklist, payroll onboarding brief). Every thread persists per task so you can pick up tomorrow exactly where you left off.",
    cta: { label: "Try a live demo", to: "/demo/service-agents" },
  },
  {
    icon: TrendingUp,
    eyebrow: "Track",
    title: "Budget, burn-rate, runway",
    body: "Inline planned-vs-actual on every line. Receipt scanning. Per-month check-ins, blended runway, and a Pricing Scenario Bar to compare tactical price moves side-by-side.",
    img: "07-budget.jpg",
    imgAlt: "Three Little Birds Budget page with planned-vs-actual columns, receipt scanning and a blended runway strip",
  },
  {
    icon: ShieldCheck,
    eyebrow: "De-risk",
    title: "Jurisdiction-aware compliance tracker",
    body: "Every plan comes with a live compliance checklist scoped to your city, county, state and industry — permits, licenses, tax registrations, zoning, health-department items and renewal deadlines. Group by status (not-started / in-progress / done / N-A), export the whole list to CSV or bring due-dates into your calendar via .ics.",
    img: "19-compliance.jpg",
    imgAlt: "Three Little Birds compliance tracker listing jurisdiction-specific licenses and permits with due dates",
  },
  {
    icon: Globe,
    eyebrow: "Fund",
    title: "Grants finder — federal, state, local & private",
    body: "One click matches your industry, location and stage against four live sources: Grants.gov (federal, open opportunities), SAM.gov Assistance Listings (federal authoritative catalog), Perplexity web citations (state, local, city, county and private/foundation/corporate programs) and a Three Little Birds matcher that scores every hit against your business. Save the ones you like and generate a per-grant application draft in minutes.",
    img: "20-grants.jpg",
    imgAlt: "Three Little Birds grants finder showing matches from Grants.gov, SAM.gov, Three Little Birds-suggested and web-cited private programs",
  },
  {
    icon: Globe,
    eyebrow: "Validate",
    title: "Market research with live citations",
    body: "One click pulls a web-sourced market report into your plan — TAM/SAM/SOM sizing, growth trends, competitor landscape and demand signals — with every figure backed by a real, clickable source. It rides along into your pitch deck (PDF + PowerPoint). Standard+.",
  },
  {
    icon: Users,
    eyebrow: "Capitalize",
    title: "Investor marketplace + per-section engagement",
    body: "Founders flip a switch when they're ready. Investors browse a curated read-only directory. Founders see exactly which sections each investor is reading.",
  },
  {
    icon: Briefcase,
    eyebrow: "Sell",
    title: "Native CRM + deal pipeline",
    body: "Standard+ drops a full sales OS into your plan: contacts, companies, deals and a drag-and-drop deal pipeline (Lead → Qualified → Proposal → Negotiation → Won / Lost). Closed-won deals feed straight into your Goals and Runway, so your projection stays honest as the pipeline moves. Segment views break the pipeline down by customer type so you see exactly where your revenue is really coming from.",
    img: "18-crm.jpg",
    imgAlt: "Three Little Birds CRM deal pipeline with Lead, Qualified, Proposal, Negotiation and Won columns full of real deals",
  },
  {
    icon: FolderOpen,
    eyebrow: "Store",
    title: "Document Library with research",
    body: "A tag-and-folder document vault for contracts, invoices, licenses and receipts. Drop in a PDF or image and Three Little Birds extracts key terms, dates and amounts so you can search across every file — no more digging through email attachments when the accountant asks.",
  },
  {
    icon: Landmark,
    eyebrow: "Borrow",
    title: "Loan modeler + bank-ready package",
    body: "Model any loan side-by-side against your budget: enter amount, term, APR and equity injection and we compute the monthly payment, DSCR, break-even and the runway impact — instantly. When you're ready, one click builds a lender package (3-statement financials, Sources & Uses, borrower forms, cover letter and industry benchmark page) as a single bank-formatted PDF, plus a full .docx of your plan, all e-mailable straight to the banker with a delivery history log.",
    img: "17-loans.jpg",
    imgAlt: "Three Little Birds Loans page with amount, term, APR, equity injection and a bank-ready PDF export flow",
  },
  {
    icon: RefreshCw,
    eyebrow: "Reconcile",
    title: "QuickBooks & Xero sync",
    body: "Standard+ connects QuickBooks Online or Xero in one click. We pull your last 30 days of transactions — read-only, never writing back — and merge them into your Budget Actuals per month. No accounting tool? A guided CSV import gets you the same result.",
  },
  {
    icon: Landmark,
    eyebrow: "Finance",
    title: "Lender-ready financial package",
    body: "Standard+ turns your plan and budget into the documents a bank needs: a 3-statement model (P&L, cash flow, a balance sheet that balances), Sources & Uses, break-even and DSCR — plus blank borrower forms — exported as one bank-formatted PDF.",
  },
  {
    icon: Send,
    eyebrow: "Reach",
    title: "Marketing Journeys — email + SMS on autopilot",
    body: "Advanced turns your contacts into orchestrated outreach. Build reusable Email & SMS templates with click-to-insert placeholders ({{first_name}}, {{business_name}} and four more), then chain them into multi-step Marketing Journeys — 5 node types (Email · SMS · Wait · Split · End) on a drag-and-drop canvas, triggered by manual enrolment, new-contact creation, entry into any of 8 built-in smart segments, or entry into a saved Contact Group. A live Cost Guard forecasts every send against your global monthly Send Budget AND your per-segment sub-caps — anything that would exceed either cap is blocked before it drains your quota. Each campaign tracks Delivered / Opened / Clicked with pixel-based open tracking and redirect-based click tracking. Real SMS + provider-delivered email out of the box, workspace-wide dashboard rolls up every plan.",
  },
  {
    icon: Megaphone,
    eyebrow: "Create",
    title: "Social Media Ad Copy — drafts for Meta, Google, TikTok & LinkedIn",
    body: "Generate platform-native social ad copy in seconds. Pick a product/service, tone and audience — Ad Copy Studio drafts 3 variants per platform (Meta, Google, TikTok, LinkedIn) with headlines, primary text, CTAs, hashtags and character-limit guards baked in so nothing overflows. Export as CSV with UTM tags auto-appended (utm_campaign=draft id, utm_content=variant idx) so every paid click routes straight into the CRM's attribution loop — no manual tagging, no spreadsheet stitching.",
  },
  {
    icon: TrendingUp,
    eyebrow: "Prove ROI",
    title: "Ads Tracking + CRM Attribution — close the loop from ad → deal → revenue",
    body: "Paste your Meta Pixel and Google Ads conversion IDs once and Three Little Birds emits a copy-paste tracking snippet. Every closed-won deal can be attributed back to a specific Ad Copy Studio draft × variant — automatically via UTM parsing, manually via a draft picker, or in bulk via multi-select on the pipeline. See a Top-5 leaderboard, a 0–100 Attribution Health Score with a 12-week sparkline, and one-tap filter pills on the plan dashboard that drill straight into the deals a given ad produced. Meta Conversions API (server-side Purchase events) fires automatically when a deal flips to Won so your bidding optimises for real revenue, not just form fills — SHA-256-hashed PII, dedup via event_id, and a Replay button on the Attribution page to backfill historical wins. Every bulk action has a 24-hour Undo (72h on Advanced+) so a fat-finger is a one-click recovery.",
  },
  {
    icon: BarChart3,
    eyebrow: "Forecast",
    title: "Driver forecasting + industry benchmarks",
    body: "Advanced builds a bottom-up 3-year monthly P&L from real drivers — revenue streams, hires, operating expenses synced one-click from your Budget — exportable as a 36-month CSV, with layered what-if scenarios on top. Standard+ also compares your margins, labor, rent and growth against vetted industry ranges, flags assumptions that look off, and deep-links you straight to the line that needs fixing.",
    img: "12-forecast.jpg",
    imgAlt: "Three Little Birds Driver Forecast chart showing a 36-month revenue projection with contribution and gross-margin overlays",
  },
  {
    icon: LayoutTemplate,
    eyebrow: "Start fast",
    title: "Business Plan Examples library",
    body: "Don't start from a blank page. Browse 50 complete, polished example plans across industries — cafe, retail, SaaS, services, trades, manufacturing and more — preview the full strategy, financials and roadmap, then clone one into your workspace and make it yours in a single click.",
    cta: { label: "See real example plans", to: "/examples" },
  },
  {
    icon: FlaskConical,
    eyebrow: "Operate",
    title: "Product & service feasibility",
    body: "Launching isn't the finish line. Standard+ lets you pressure-test any new or existing product or service — we compute the unit economics (contribution margin, monthly contribution, break-even) and hand you a grounded go / caution / no-go verdict with the highest-leverage moves to make next. Validate before you build, and optimize what you already run.",
  },
  {
    icon: Target,
    eyebrow: "Operate",
    title: "Business optimization",
    body: "Stop guessing where the leverage is. We read your plan, your actuals and your industry benchmarks and hand back a prioritized list of the highest-impact cost, pricing and margin moves — each with the expected effect, so you act on the few things that matter most.",
  },
  {
    icon: Repeat,
    eyebrow: "Operate",
    title: "Continuous process improvement",
    body: "Run your business like a system that compounds. Log the processes that matter, run improvement cycles (plan → change → measure) and track the real impact against a baseline over time — so every month you operate a little sharper than the last.",
  },
];

/* ------------------------- Feature Film teaser ----------------------- */
// A large editorial poster card that previews the embedded explainer
// film. Plays the same FeatureVideo modal as Hero's "Watch the intro"
// link — keeping the marketing surface and the login experience in sync.
function FeatureFilm({ onPlayVideo }) {
  return (
    <section
      className="relative bg-neutral-950 text-white overflow-hidden scroll-mt-20"
      id="product"
      data-testid="landing-feature-film"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none landing-film-ambient"
      />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* Left: copy */}
          <div className="lg:col-span-5">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#7C5FB0] mb-5 inline-flex items-center gap-2">
              <PlayCircle className="w-3 h-3" />
              The feature film · 60 seconds
            </div>
            <h2
              className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.95]"
              data-testid="landing-feature-film-headline"
            >
              See it in motion.
            </h2>
            <p className="mt-5 text-base sm:text-lg text-neutral-300 max-w-md leading-relaxed">
              A one-minute walk-through of the wizard, the Three Little Birds-drafted plan,
              the live roadmap, and the launch-readiness score — exactly
              what your first ten minutes inside Three Little Birds feel
              like.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={onPlayVideo}
                data-testid="landing-feature-film-play-btn"
                className="h-12 px-6 inline-flex items-center gap-2 bg-[#7C5FB0] hover:bg-[#5E4689] text-white text-[12px] uppercase tracking-[0.25em] font-bold transition-colors"
              >
                <Play className="w-4 h-4" />
                Play the film
              </button>
              <Link
                to="/login"
                data-testid="landing-feature-film-cta-skip"
                className="h-12 px-2 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.25em] font-bold text-neutral-300 hover:text-white"
              >
                Skip & start your plan
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

          {/* Right: cinematic poster that doubles as the play affordance.
              Clicking anywhere inside opens the same modal as the button. */}
          <div className="lg:col-span-7">
            <button
              type="button"
              onClick={onPlayVideo}
              data-testid="landing-feature-film-poster"
              aria-label="Play the feature film"
              className="group relative w-full aspect-video block overflow-hidden border-2 border-white/15 hover:border-[#7C5FB0] transition-colors focus:outline-none focus:border-[#7C5FB0]"
            >
              <img
                src="/features-aisha-poster.jpg?v=1"
                alt=""
                className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.02] transition-all duration-700"
                draggable={false}
                loading="lazy"
              />
              <div
                aria-hidden
                className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"
              />
              {/* Centered play badge */}
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-[#7C5FB0]/95 group-hover:bg-[#7C5FB0] flex items-center justify-center shadow-[0_24px_48px_rgba(124,95,176,0.45)] transition-transform group-hover:scale-105">
                  <Play className="w-8 h-8 sm:w-10 sm:h-10 text-white translate-x-0.5" />
                </span>
              </span>
              {/* Lower-third caption */}
              <span className="absolute bottom-0 left-0 right-0 p-5 sm:p-7 flex items-end justify-between gap-3">
                <span className="text-left">
                  <span className="block text-[9px] sm:text-[10px] uppercase tracking-[0.3em] font-bold text-[#7C5FB0] mb-1.5">
                    Trailer
                  </span>
                  <span className="block font-display font-black tracking-tighter text-xl sm:text-2xl">
                    Don&rsquo;t worry about a thing.
                  </span>
                </span>
                <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/70 tabular-nums">
                  01:00
                </span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function FeatureGrid() {
  return (
    <section
      className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 scroll-mt-20"
      id="features"
      data-testid="landing-features"
    >
      {/* Feb 2026 · ambient backdrop — same painterly lilac wash the
          Hub uses so the marketing surface reads as part of the same
          product, not a bolted-on brochure. Fully decorative. */}
      <div
        aria-hidden="true"
        data-testid="landing-features-ambient"
        className="absolute inset-0 pointer-events-none landing-features-ambient-bg"
      />
      <div className="relative max-w-3xl">
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#7C5FB0] mb-3 inline-flex items-center gap-2">
          <Sparkles className="w-3 h-3" />
          Six pillars
        </div>
        <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
          Everything you need before you spend a dollar.
        </h2>
        <p className="mt-6 text-lg text-neutral-600 leading-relaxed">
          One tool that walks with you from &quot;I have an idea&quot; to &quot;we&apos;re hiring
          our second engineer.&quot; No more cobbling together docs, sheets,
          checklists, and pitch decks.
        </p>
      </div>
      <div className="relative mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-neutral-200">
        {FEATURES.map((f, i) => (
          <FeatureCard key={f.title} feature={f} index={i} />
        ))}
      </div>

      {/* Feb 26 2026 · Marketing Journeys spotlight — teaches the
          canvas-based journey builder + Cost Guard visually. Sits
          above the Attribution spotlight so the reading order is
          Reach → Prove ROI, matching the founder's month-over-month
          workflow. */}
      <div
        className="relative mt-16 grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-14 items-center"
        data-testid="landing-marketing-journeys-spotlight"
      >
        <MarketingJourneyDemoReel />
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#7C5FB0] mb-3 inline-flex items-center gap-2">
            <Send className="w-3 h-3" />
            Marketing Journeys
          </div>
          <h3 className="font-display font-black text-3xl sm:text-4xl tracking-tighter leading-[1.1] mb-4">
            One canvas. Every touchpoint. Zero spreadsheets.
          </h3>
          <p className="text-neutral-600 leading-relaxed mb-4">
            Chain email, SMS and wait steps into a live Marketing Journey that fires when a
            new contact lands in a smart segment &mdash; then let a real-time
            {" "}<span className="font-black">Cost Guard</span> forecast every send against
            your monthly budget and per-segment sub-caps before a single message goes out.
          </p>
          <ul className="space-y-2 text-sm text-neutral-700">
            {[
              "5 node types (Email · SMS · Wait · Split · End) on a drag-and-drop canvas",
              "Triggered by manual enrolment, new-contact creation, smart segments, or Contact Groups",
              "Placeholders like {{first_name}} + {{business_name}} — click-to-insert, no memorising",
              "Real SMS + provider-delivered email · pixel opens · redirect click tracking",
              "Cost Guard blocks any send that would blow your monthly Send Budget",
            ].map((line) => (
              <li key={line} className="flex items-start gap-2">
                <span className="mt-1 w-1.5 h-1.5 bg-[#7C5FB0] shrink-0" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Feb 26 2026 · Attribution loop spotlight — teaches the
          leaderboard → drill-through → undo flow visually before the
          founder ever creates an account. Pairs with the two new
          marketing feature cards above. */}
      <div
        className="relative mt-16 grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-14 items-center"
        data-testid="landing-attribution-spotlight"
      >
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#7C5FB0] mb-3 inline-flex items-center gap-2">
            <Megaphone className="w-3 h-3" />
            The attribution loop
          </div>
          <h3 className="font-display font-black text-3xl sm:text-4xl tracking-tighter leading-[1.1] mb-4">
            From ad draft to won revenue &mdash; in one product.
          </h3>
          <p className="text-neutral-600 leading-relaxed mb-4">
            Draft platform-native ads in seconds, ship them with UTMs baked in,
            and watch every closed-won deal trace itself back to the exact
            creative that produced it. Founders on <span className="font-bold">Advanced</span>
            {" "}get a live Top-5 leaderboard, a 0&ndash;100 Attribution Health Score,
            filter pills for one-tap drill-through, and a 24&ndash;72 hour Undo on
            every bulk action.
          </p>
          <ul className="space-y-2 text-sm text-neutral-700">
            {[
              "3 platform-native variants per platform (Meta, Google, TikTok, LinkedIn)",
              "UTM auto-appending + CRM deal attribution (auto · manual · bulk)",
              "Meta Conversions API server-side Purchase events",
              "Attribution Health Score + 12-week sparkline trend",
              "24h Undo (72h on Advanced) — admin-tunable per tier",
            ].map((line) => (
              <li key={line} className="flex items-start gap-2">
                <span className="mt-1 w-1.5 h-1.5 bg-[#7C5FB0] shrink-0" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
        <AttributionDemoReel />
      </div>
    </section>
  );
}

function FeatureCard({ feature, index }) {
  const Icon = feature.icon;
  return (
    <div
      data-testid={`landing-feature-${index}`}
      className="group relative border-r border-b border-neutral-200 p-8 transition-all duration-300 hover:bg-gradient-to-br hover:from-white hover:to-[#F5F1FB]"
    >
      {/* Feb 2026 · glass halo on hover — mirrors the Hub section cards
          so the marketing pillar grid reads as a preview of the
          in-product surface, not a different design system. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300 landing-pillar-halo"
      />
      <div className="relative">
        <div className="w-10 h-10 inline-flex items-center justify-center bg-[#7C5FB0] text-white shadow-[0_4px_14px_0_rgba(124,95,176,0.35)] group-hover:shadow-[0_6px_18px_0_rgba(124,95,176,0.55)] transition-shadow duration-300">
          <Icon className="w-5 h-5" />
        </div>
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-neutral-500 mt-5">
          {feature.eyebrow}
        </div>
        <h3 className="font-display font-black text-2xl tracking-tighter mt-1.5 leading-tight">
          {feature.title}
        </h3>
        <p className="text-sm text-neutral-600 leading-relaxed mt-3">
          {feature.body}
        </p>
        {feature.img && (
          // Jul 2026 — some feature cards embed a real screenshot of the
          // shipped surface (same asset the /60-sec demo uses) so anonymous
          // visitors see the product without opening the modal.
          // Feb 2026 · softer frame — replaced the brutal 2px-black +
          // hard shadow with a lilac ring + soft glow to match the Hub
          // glass-halo aesthetic. Lifts subtly on hover.
          <div className="mt-5 border border-[#E8DFF5] shadow-[0_8px_24px_-8px_rgba(124,95,176,0.35)] group-hover:shadow-[0_14px_32px_-8px_rgba(124,95,176,0.55)] group-hover:-translate-y-0.5 transition-all duration-300 overflow-hidden bg-white">
            <img
              src={`/demo/${feature.img}`}
              alt={feature.imgAlt || feature.title}
              loading="lazy"
              className="w-full h-auto block"
              data-testid={`landing-feature-${index}-img`}
            />
          </div>
        )}
        {feature.cta && (
          <Link
            to={feature.cta.to}
            data-testid={`landing-feature-${index}-cta`}
            className="mt-4 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] font-bold text-[#7C5FB0] hover:text-[#5E4689] transition-colors"
          >
            {feature.cta.label}
            <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
          </Link>
        )}
      </div>
    </div>
  );
}

/* --------------------------- How it works --------------------------- */

function HowItWorks() {
  const steps = [
    {
      n: "01",
      title: "Five-step wizard",
      body: "Name, offering, industry, target market, expected revenue. ~2 minutes.",
    },
    {
      n: "02",
      title: "drafts your plan",
      body: "Pricing model, revenue projection, competitive grid, customer acquisition, infrastructure — all live in under 60s.",
    },
    {
      n: "03",
      title: "Iterate, execute, capitalize",
      body: "Tighten sections, log actuals, generate the launch board, list to investors when your Health Score crosses 50.",
    },
  ];
  return (
    <section
      className="bg-[#0A0A0A] text-white py-24 lg:py-32"
      data-testid="landing-how"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#7C5FB0] mb-3">
          How it works
        </div>
        <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter max-w-3xl leading-[1.05]">
          Three steps from your notes app to a real plan.
        </h2>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-0 border-l border-neutral-800">
          {steps.map((s, i) => (
            <div
              key={s.n}
              className="border-r border-neutral-800 p-8"
              data-testid={`landing-step-${i}`}
            >
              <div className="font-display font-black text-6xl tracking-tighter text-[#7C5FB0] leading-none">
                {s.n}
              </div>
              <h3 className="font-display font-black text-2xl tracking-tighter mt-6">
                {s.title}
              </h3>
              <p className="text-sm text-neutral-400 leading-relaxed mt-3">
                {s.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------------------- Social proof --------------------------- */

const TESTIMONIALS = [
  {
    quote:
      "I went from scribbled notes in Notion to a 14-section plan and a real budget in an afternoon. The hybrid health score finally told me what was actually weak.",
    name: "Maya R.",
    role: "Founder, boutique consultancy",
  },
  {
    quote:
      "The compliance tracker alone saved me a $400 lawyer call. Add the grants finder and the budget burn-rate panel and it's already paid for itself ten times over.",
    name: "Devon S.",
    role: "Founder, food-truck startup",
  },
  {
    quote:
      "Listing on the investor marketplace and seeing per-section engagement counts is genuinely a new experience for me as a founder. I know what they care about.",
    name: "Priya N.",
    role: "Founder, B2B SaaS",
  },
];

function SocialProof() {
  return (
    <section
      className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32"
      data-testid="landing-social-proof"
    >
      <div className="max-w-3xl">
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-neutral-500 mb-3 inline-flex items-center gap-2">
          <Star className="w-3 h-3 fill-current text-[#F59E0B]" />
          Founders are shipping
        </div>
        <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
          Built with founders. Used by founders.
        </h2>
      </div>
      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-l border-neutral-200">
        {TESTIMONIALS.map((t, i) => (
          <figure
            key={t.name}
            className="border-r border-b border-neutral-200 p-8 flex flex-col"
            data-testid={`landing-testimonial-${i}`}
          >
            <blockquote className="font-display text-xl tracking-tight leading-snug flex-1">
              &ldquo;{t.quote}&rdquo;
            </blockquote>
            <figcaption className="mt-6 pt-6 border-t border-neutral-100">
              <div className="font-bold text-sm">{t.name}</div>
              <div className="text-xs text-neutral-500 mt-0.5">{t.role}</div>
            </figcaption>
          </figure>
        ))}
      </div>
      <MarketplaceHighlight />
    </section>
  );
}

function MarketplaceHighlight() {
  // Live insights — fetches up to 5 anonymized comparative reads from
  // /public/marketplace-pulse and rotates through them every
  // `rotation_seconds`. Falls back to a static sample line when:
  //   - the API is unreachable / errors
  //   - the marketplace has fewer than 2 actively-engaged plans
  //     (privacy guard on the backend keeps niche-industry founders
  //      from being re-identified by the landing-page tile)
  // The static fallback mirrors the in-app insight format so the
  // tile always *demonstrates* the engagement signal even on empty.
  const [insights, setInsights] = useState([]);
  const [rotationSeconds, setRotationSeconds] = useState(6);
  const [activeIdx, setActiveIdx] = useState(0);
  // Brief opacity fade between rotations — pure CSS transition, no
  // animation library so the marketing surface stays light.
  const [fading, setFading] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/public/marketplace-pulse");
        if (!alive) return;
        setInsights(Array.isArray(data?.insights) ? data.insights : []);
        if (typeof data?.rotation_seconds === "number") {
          setRotationSeconds(Math.max(3, data.rotation_seconds));
        }
      } catch {
        if (alive) setInsights([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Rotation timer — runs only when ≥2 insights are available.
  // 200ms fade-out → swap → fade-in keeps the transition snappy.
  useEffect(() => {
    if (insights.length < 2) return undefined;
    const interval = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setActiveIdx((i) => (i + 1) % insights.length);
        setFading(false);
      }, 200);
    }, rotationSeconds * 1000);
    return () => clearInterval(interval);
  }, [insights.length, rotationSeconds]);

  // Static fallback when the API returned nothing — keeps the tile
  // visually anchored and demonstrates the engagement format.
  const isLive = insights.length > 0;
  const current = isLive
    ? insights[activeIdx]
    : { category: "Sample", insight: "Investors are clicking Burn rate 3× more than Compliance this week." };

  return (
    <div
      className="mt-12 border-2 border-[#7C5FB0] bg-gradient-to-br from-[#F5F0FB] to-white p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center"
      data-testid="landing-marketplace-highlight"
    >
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#7C5FB0] mb-3 inline-flex items-center gap-2">
          <Eye className="w-3 h-3" />
          Investor Marketplace
        </div>
        <h3 className="font-display font-black text-3xl sm:text-4xl tracking-tighter leading-[1.05]">
          The only plan tool that closes the loop with your investors.
        </h3>
        <p className="text-base text-neutral-700 leading-relaxed mt-5 max-w-xl">
          Standard+ founders flip a switch and their plan goes live for
          investors who match their industry. Per-section view counters tell
          you exactly which parts of the plan are pulling investor attention
          this week.
        </p>
      </div>
      <div
        className="border border-neutral-300 bg-white p-6 space-y-3 relative"
        data-testid="landing-marketplace-pulse-card"
      >
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#7C5FB0]">
            {isLive ? `${current.category} · This week` : "Marketplace · This week"}
          </div>
          {isLive && (
            <div
              className="flex items-center gap-1.5"
              data-testid="landing-marketplace-pulse-live"
              aria-label="Live data — rotating insights"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#059669] opacity-60" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#059669]" />
              </span>
              <span className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#059669]">
                Live
              </span>
            </div>
          )}
        </div>
        <div
          className={`font-display font-black text-lg tracking-tighter leading-snug transition-opacity duration-200 ${fading ? "opacity-0" : "opacity-100"}`}
          data-testid="landing-marketplace-pulse-insight"
        >
          {current.insight}
        </div>
        {isLive && insights.length > 1 && (
          <div
            className="flex items-center gap-1 pt-3 border-t border-neutral-100"
            data-testid="landing-marketplace-pulse-dots"
            role="tablist"
            aria-label="Rotation progress"
          >
            {insights.map((_, i) => (
              <span
                key={i}
                aria-label={`Insight ${i + 1} of ${insights.length}`}
                className={`h-1 transition-all duration-300 ${
                  i === activeIdx
                    ? "w-6 bg-[#7C5FB0]"
                    : "w-3 bg-neutral-200"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* -------------------------- Pricing teaser -------------------------- */

const PLANS = [
  {
    tier: "Basic",
    price: "$15",
    cycle: "/mo",
    body: "Plan + roadmap + watermarked PDF. Solo founders piloting an idea.",
    perks: ["Three Little Birds plan draft", "Launch board", "Watermarked PDF export"],
    accent: "#A3A3A3",
  },
  {
    tier: "Standard",
    price: "$25",
    cycle: "/mo",
    body: "Everything in Basic plus budget tracking, compliance, CRM, documents, lender package, benchmarks, and the investor marketplace.",
    perks: [
      "Budget & burn-rate",
      "QuickBooks & Xero sync",
      "Compliance tracker",
      "Native CRM & pipeline",
      "Document Library",
      "Lender-ready financial package",
      "Industry benchmarks + snapshot PDF",
      "Investor marketplace listing",
      "Clean PDF + customizable Pitch Deck",
    ],
    accent: "#7C5FB0",
    badge: "Most popular",
    featured: true,
  },
  {
    tier: "Advanced",
    price: "$35",
    cycle: "/mo",
    body: "Everything in Standard plus driver-based forecasting, what-if scenarios, investor scenarios, and Email & SMS campaigns.",
    perks: [
      "Driver-based forecasting (36-mo CSV)",
      "What-if scenario planner",
      "Investor scenarios (equity / income / hybrid)",
      "Email & SMS campaigns",
      "Unlimited business plans",
      "Priority support",
    ],
    accent: "#059669",
  },
];

// B2B / white-label tiers surfaced beneath the three consumer plans. Mirrors
// the advisor entries in backend/plans.py PLAN_CATALOG + the Billing page's
// AdvisorCard so the marketing surface stays in lock-step.
const ADVISOR_PLANS = [
  {
    tier: "advisor_starter",
    label: "Advisor Starter",
    price: "$149",
    cycle: "/mo",
    seats: "10",
    ownPlans: "5",
    body: "White-label the whole platform for up to 10 client founders.",
    perks: [
      "Your logo + colors on client PDFs, decks & share cards",
      "Co-branded client login & invite landing pages",
      "Cohort dashboard with per-client health scores",
      "Email-invite onboarding (auto-copy link, 14-day expiry)",
      "Full Standard features on every client seat",
      "10 Three Little Birds logo generations across client brands",
    ],
    accent: "#0E7490",
  },
  {
    tier: "advisor_pro",
    label: "Advisor Pro",
    price: "$299",
    cycle: "/mo",
    seats: "25",
    ownPlans: "5",
    body: "Up to 25 clients — plus investor scenarios, what-if & campaigns.",
    perks: [
      "Everything in Advisor Starter, for 25 client seats",
      "Investor scenarios, what-if planner & Email/SMS campaigns",
      "Driver-based forecasting on every client plan",
      "25 Three Little Birds logo generations across client brands",
    ],
    accent: "#5E4689",
    badge: "Most cohorts",
    featured: true,
  },
];

function PricingTeaser() {
  return (
    <section
      className="bg-neutral-50 py-24 lg:py-32 scroll-mt-20"
      id="pricing"
      data-testid="landing-pricing"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-neutral-500 mb-3">
            Pricing
          </div>
          <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
            Three tiers. Try the full experience free for seven days first.
          </h2>
          <p className="mt-5 text-base text-neutral-600 leading-relaxed max-w-2xl">
            Every new account starts on a 7-day preview tier — no card
            required. Generate a plan, explore every section, then choose the
            tier that fits when the preview ends.
          </p>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-l border-neutral-900">
          {PLANS.map((p, i) => (
            <div
              key={p.tier}
              data-testid={`landing-plan-${p.tier.toLowerCase()}`}
              className={`border-r border-b border-neutral-900 p-8 flex flex-col bg-white ${p.featured ? "relative" : ""}`}
            >
              {p.badge && (
                <span
                  className="absolute -top-3 left-8 bg-[#7C5FB0] text-white text-[9px] uppercase tracking-[0.25em] font-bold px-2 py-1"
                  data-testid={`landing-plan-${p.tier.toLowerCase()}-badge`}
                >
                  {p.badge}
                </span>
              )}
              <div
                className="text-[10px] uppercase tracking-[0.3em] font-bold"
                style={{ color: p.accent }}
              >
                {p.tier}
              </div>
              <div className="mt-3 flex items-baseline gap-1">
                <div className="font-display font-black text-5xl tracking-tighter">
                  {p.price}
                </div>
                <div className="text-sm text-neutral-500">{p.cycle}</div>
              </div>
              <p className="text-sm text-neutral-600 mt-3 leading-relaxed">
                {p.body}
              </p>
              <ul className="mt-6 space-y-2.5 text-sm flex-1">
                {p.perks.map((perk) => (
                  <li
                    key={perk}
                    className="flex items-start gap-2"
                    data-testid={`landing-plan-${p.tier.toLowerCase()}-perk`}
                  >
                    <Check
                      className="w-4 h-4 flex-shrink-0 mt-0.5"
                      style={{ color: p.accent }}
                    />
                    <span>{perk}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/login"
                data-testid={`landing-plan-${p.tier.toLowerCase()}-cta`}
                className={`mt-8 inline-flex items-center justify-center gap-2 h-11 text-[11px] uppercase tracking-[0.25em] font-bold transition-colors ${
                  p.featured
                    ? "bg-[#7C5FB0] hover:bg-[#5E4689] text-white"
                    : "bg-white border border-neutral-900 hover:bg-neutral-900 hover:text-white"
                }`}
              >
                Start with {p.tier}
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          ))}
        </div>

        {/* For advisors & agencies — white-label / multi-client tiers */}
        <div className="mt-20" data-testid="landing-advisor-section">
          <div className="flex items-baseline gap-4 flex-wrap">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#7C5FB0]">
              For advisors & agencies
            </div>
            <h3 className="font-display font-black text-2xl sm:text-4xl tracking-tighter leading-[1.05]">
              White-label the platform for your clients.
            </h3>
          </div>
          <p className="mt-4 text-base text-neutral-600 leading-relaxed max-w-2xl">
            Pay one seat fee, invite your founders, and they get the platform
            branded with your logo + colors — at no extra cost to them. Their
            PDFs, share cards and invite landing pages all carry your brand.
          </p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-l border-neutral-900">
            {ADVISOR_PLANS.map((p) => (
              <div
                key={p.tier}
                data-testid={`landing-advisor-${p.tier}`}
                className={`border-r border-b border-neutral-900 p-8 flex flex-col relative ${p.featured ? "bg-[#0A0A0A] text-white" : "bg-white"}`}
              >
                {p.badge && (
                  <span
                    className="absolute -top-3 left-8 bg-[#7C5FB0] text-white text-[9px] uppercase tracking-[0.25em] font-bold px-2 py-1"
                    data-testid={`landing-advisor-${p.tier}-badge`}
                  >
                    {p.badge}
                  </span>
                )}
                <div
                  className="text-[10px] uppercase tracking-[0.3em] font-bold"
                  style={{ color: p.featured ? "#A78BFA" : p.accent }}
                >
                  {p.label}
                </div>
                <div className="mt-3 flex items-baseline gap-1">
                  <div className="font-display font-black text-5xl tracking-tighter">
                    {p.price}
                  </div>
                  <div className={`text-sm ${p.featured ? "text-white/60" : "text-neutral-500"}`}>{p.cycle}</div>
                </div>
                <p className={`text-sm mt-3 leading-relaxed ${p.featured ? "text-white/70" : "text-neutral-600"}`}>
                  {p.body}
                </p>
                <div className="mt-5 grid grid-cols-2 gap-3">
                  <div className={`p-3 ${p.featured ? "bg-white/10" : "bg-neutral-100"}`} data-testid={`landing-advisor-${p.tier}-seats`}>
                    <div className="text-[10px] uppercase tracking-[0.2em] opacity-60">Client seats</div>
                    <div className="font-display font-black text-2xl tabular-nums mt-0.5">{p.seats}</div>
                  </div>
                  <div className={`p-3 ${p.featured ? "bg-white/10" : "bg-neutral-100"}`}>
                    <div className="text-[10px] uppercase tracking-[0.2em] opacity-60">Your own plans</div>
                    <div className="font-display font-black text-2xl tabular-nums mt-0.5">{p.ownPlans}</div>
                  </div>
                </div>
                <ul className="mt-6 space-y-2.5 text-sm flex-1">
                  {p.perks.map((perk) => (
                    <li
                      key={perk}
                      className="flex items-start gap-2"
                      data-testid={`landing-advisor-${p.tier}-perk`}
                    >
                      <Check
                        className="w-4 h-4 flex-shrink-0 mt-0.5"
                        style={{ color: p.featured ? "#A78BFA" : p.accent }}
                      />
                      <span>{perk}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/login"
                  data-testid={`landing-advisor-${p.tier}-cta`}
                  className={`mt-8 inline-flex items-center justify-center gap-2 h-11 text-[11px] uppercase tracking-[0.25em] font-bold transition-colors ${
                    p.featured
                      ? "bg-[#7C5FB0] hover:bg-[#5E4689] text-white"
                      : "bg-white border border-neutral-900 hover:bg-neutral-900 hover:text-white"
                  }`}
                >
                  Start {p.label}
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            ))}
          </div>
        </div>

        <PlanComparisonMatrix className="mt-16" />

        <div className="mt-20 scroll-mt-20" id="compare" data-testid="landing-competitor-section">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#7C5FB0] font-bold mb-2">
            Coming from another tool?
          </div>
          <h3 className="font-display font-black text-2xl sm:text-3xl tracking-tighter">
            Everything LivePlan does — plus an execution OS, for less.
          </h3>
          <p className="text-sm text-neutral-600 mt-2 max-w-2xl">
            We match the planning and forecasting tools founders switch for — then add the roadmap, CRM,
            campaigns, grants and Founder Mentor that take you all the way to launch.
          </p>
          <CompetitorMatrix competitor="liveplan" className="mt-6" />
          <div className="mt-5 flex flex-wrap items-center gap-4 text-[11px] uppercase tracking-[0.25em] font-bold">
            <Link to="/vs-liveplan" className="inline-flex items-center gap-2 text-neutral-900 hover:gap-3 transition-all" data-testid="landing-vs-liveplan-link">
              Full LivePlan comparison <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/vs-bizplan" className="text-neutral-500 hover:text-neutral-900" data-testid="landing-vs-bizplan-link">vs. Bizplan</Link>
            <Link to="/vs-enloop" className="text-neutral-500 hover:text-neutral-900" data-testid="landing-vs-enloop-link">vs. Enloop</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section
      className="bg-[#059669] text-white py-20 lg:py-28"
      data-testid="landing-final-cta"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
        <div className="lg:col-span-2">
          <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.0]">
            Stop noodling. Start shipping.
          </h2>
          <p className="text-emerald-100 text-lg mt-4 max-w-2xl">
            Seven-day preview tier on every new account, no card required.
            Generate your plan, see every section, decide later.
          </p>
        </div>
        <div className="flex flex-col gap-3">
          <Link to="/login" data-testid="landing-final-cta-primary">
            <Button className="rounded-none w-full bg-white text-[#059669] hover:bg-neutral-100 h-14 px-6 text-[13px] uppercase tracking-[0.25em] font-black">
              Start your free preview
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <Link
            to="/login"
            data-testid="landing-final-cta-secondary"
            className="text-center text-emerald-100 hover:text-white text-[11px] uppercase tracking-[0.25em] font-bold"
          >
            Already have an account? Sign in →
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ About / FAQ ------------------------------ */

const ABOUT_FAQS = [
  {
    q: "What is Three Little Birds?",
    a: "Three Little Birds is an all-in-one \u201Cbusiness launch and operate OS\u201D for first-time founders and small businesses. It pairs Three Little Birds business-plan writing and lender-grade financial modeling with a full execution layer \u2014 roadmap, CRM, campaigns, grants, compliance, and an Founder Mentor \u2014 so you go from idea to launch to growth in one tool.",
  },
  {
    q: "Who is it for?",
    a: "First-time founders, small-business owners, and the advisors and accelerators who serve them \u2014 anyone who wants a guided, structured path instead of a blank document.",
  },
  {
    q: "How much does it cost?",
    a: "Every new account gets a free 7-day full preview (no credit card). Paid plans are Basic $15/mo, Standard $25/mo, and Advanced $35/mo, with Advisor / white-label tiers for firms serving multiple clients.",
  },
  {
    q: "What can I create with it?",
    a: "A complete Three Little Birds-drafted business plan, lender-ready 3-statement financials with a driver-based 3-year monthly forecast and what-if scenarios, a drag-and-drop launch board (with a Gantt view for the critical path), a built-in CRM and deal pipeline, Three Little Birds-drafted ad copy for Meta / Google / TikTok / LinkedIn with server-side conversion tracking + CRM attribution, an investor pitch deck (PDF & PowerPoint), SBA-compliant lending documents, live-cited market research, and reporting dashboards.",
  },
  {
    q: "How is it different from a template or plan-only tools like LivePlan?",
    a: "Three Little Birds matches the plan-writing and forecasting of plan-only tools, then adds an execution OS they don\u2019t have \u2014 roadmap, CRM, campaigns, grants, compliance, an investor marketplace, and an Founder Mentor \u2014 usually at a lower starting price.",
  },
  {
    q: "Is Three Little Birds good for first-time founders?",
    a: "Yes \u2014 it\u2019s built for them. Instead of a blank document, you get a guided, step-by-step path: the drafts your plan, builds the financials, and lays out a launch roadmap, while a built-in Founder Mentor explains every step in plain English so non-financial founders can move with confidence.",
  },
  {
    q: "Can I create an investor pitch deck and SBA loan documents?",
    a: "Yes. You can generate an investor-ready pitch deck as both PDF and PowerPoint, plus SBA-compliant lending documents and a lender-ready 3-statement financial model \u2014 so you can approach investors and banks with a complete, professional package.",
  },
  {
    q: "Is my plan private?",
    a: "Yes. Your plan is private to your account, and you choose exactly what to share \u2014 for example, a read-only investor link or a lender document bundle.",
  },
];

function AboutFaq() {
  return (
    <section className="bg-neutral-50 border-t border-neutral-200 py-20 lg:py-28" data-testid="landing-about-faq">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 font-bold">
          About Three Little Birds
        </div>
        <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[1.05] mt-3">
          The straight answers, in plain English.
        </h2>
        <dl className="mt-12 divide-y divide-neutral-200 border-t border-neutral-200">
          {ABOUT_FAQS.map((item, i) => (
            <div key={i} className="py-6" data-testid={`about-faq-${i}`}>
              <dt className="font-display font-bold text-lg tracking-tight">{item.q}</dt>
              <dd className="text-neutral-600 mt-2 leading-relaxed">{item.a}</dd>
            </div>
          ))}
        </dl>
        <p className="text-sm text-neutral-500 mt-8">
          More questions? Visit the{" "}
          <Link to="/faq" className="font-bold text-neutral-900 underline hover:no-underline">
            Help &amp; FAQ
          </Link>{" "}
          or{" "}
          <Link to="/login" className="font-bold text-neutral-900 underline hover:no-underline">
            start your free preview
          </Link>
          .
        </p>
      </div>
    </section>
  );
}

/* ------------------------------ Footer ------------------------------ */

function Footer() {
  return (
    <footer className="border-t border-neutral-200 py-12" data-testid="landing-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-wrap items-center justify-between gap-6">
        <div className="flex items-center gap-3">
          <Logo variant="mark" height={28} />
          <span className="font-display font-black tracking-tighter text-sm">
            Three Little Birds
          </span>
        </div>
        <div className="flex items-center gap-6 text-[11px] uppercase tracking-[0.2em] font-bold text-neutral-600">
          <Link to="/faq" className="hover:text-neutral-900" data-testid="landing-footer-faq-link">
            Help &amp; FAQ
          </Link>
          <Link to="/terms" className="hover:text-neutral-900" data-testid="landing-footer-terms-link">
            Terms
          </Link>
          <Link to="/privacy" className="hover:text-neutral-900" data-testid="landing-footer-privacy-link">
            Privacy
          </Link>
          <Link to="/vs-liveplan" className="hover:text-neutral-900" data-testid="landing-footer-vs-link">
            vs. LivePlan
          </Link>
          <Link to="/vs-bizplan" className="hover:text-neutral-900" data-testid="landing-footer-vs-bizplan-link">
            vs. Bizplan
          </Link>
          <Link to="/vs-enloop" className="hover:text-neutral-900" data-testid="landing-footer-vs-enloop-link">
            vs. Enloop
          </Link>
          <Link to="/login" className="hover:text-neutral-900">
            Sign in
          </Link>
          <Link to="/login" className="hover:text-neutral-900">
            Get started
          </Link>
        </div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 font-bold">
          © {new Date().getFullYear()} Three Little Birds
        </div>
      </div>
    </footer>
  );
}
